import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Chip
} from '@mui/material';
import { PROJECT_STATUSES, getStatusText, getStatusColor, getStatusTextColor } from '../../../constants/projectStatuses';
import { PROJECT_STATUS_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = PROJECT_STATUS_RETURN_TYPES;

const ProjectStatusSelector = ({
  multiple = false,
  value = multiple ? [] : '',
  onChange,
  label = "Project Status",
  required = false,
  disabled = false,
  fullWidth = true,
  returnValue = PROJECT_STATUS_RETURN_TYPES.ID, // PROJECT_STATUS_RETURN_TYPES.ID or PROJECT_STATUS_RETURN_TYPES.NAME
  size = 'medium',
  showEmptyOption = true,
  emptyOptionLabel = "None",
  ...props
}) => {
  const projectStatusOptions = Object.entries(PROJECT_STATUSES).map(([key, statusValue]) => ({
    id: statusValue,
    name: getStatusText(statusValue)
  }));

  const handleChange = (event) => {
    const selectedValue = event.target.value;

    if (onChange) {
      if (multiple) {
        if (returnValue === PROJECT_STATUS_RETURN_TYPES.NAME) {
          const selectedNames = selectedValue.map(id => {
            const option = projectStatusOptions.find(opt => opt.id === id);
            return option?.name || '';
          }).filter(Boolean);
          onChange(selectedNames);
        } else {
          onChange(selectedValue);
        }
      } else {
        if (returnValue === PROJECT_STATUS_RETURN_TYPES.NAME) {
          const option = projectStatusOptions.find(opt => opt.id === selectedValue);
          onChange(option?.name || '');
        } else {
          onChange(selectedValue || '');
        }
      }
    }
  };

  // Find the selected status(es) for internal Select component usage
  const selectedValue = (() => {
    if (!value) return multiple ? [] : '';

    if (multiple) {
      if (returnValue === PROJECT_STATUS_RETURN_TYPES.NAME) {
        return value.map(name => {
          const option = projectStatusOptions.find(opt => opt.name === name);
          return option?.id;
        }).filter(Boolean);
      } else {
        return Array.isArray(value) ? value : [];
      }
    } else {
      if (returnValue === PROJECT_STATUS_RETURN_TYPES.NAME) {
        const option = projectStatusOptions.find(opt => opt.name === value);
        return option?.id || '';
      } else {
        return value;
      }
    }
  })();

  const renderValue = (selected) => {
    if (multiple) {
      if (!selected || selected.length === 0) {
        return <em>{emptyOptionLabel}</em>;
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((value) => {
            const option = projectStatusOptions.find(opt => opt.id === value);
            return (
              <Chip
                key={value}
                label={option?.name}
                size="small"
                sx={{
                  backgroundColor: getStatusColor(value),
                  color: getStatusTextColor(value),
                  fontWeight: 500
                }}
              />
            );
          })}
        </Box>
      );
    }

    // Handle single select rendering
    if (!selected) return '';
    const option = projectStatusOptions.find(opt => opt.id === selected);
    return option?.name || selected;
  };

  return (
    <FormControl fullWidth={fullWidth} disabled={disabled} size={size} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={multiple}
        value={selectedValue}
        onChange={handleChange}
        label={label}
        disabled={disabled}
        input={multiple ? <OutlinedInput label={label} /> : undefined}
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              zIndex: 1350 // Ensure dropdown appears above table
            }
          }
        }}
      >
        {!required && !multiple && showEmptyOption && (
          <MenuItem value="">
            <em>{emptyOptionLabel}</em>
          </MenuItem>
        )}
        {projectStatusOptions.map(option => (
          <MenuItem key={option.id} value={option.id}>
            <Chip
              label={option.name}
              size="small"
              sx={{
                backgroundColor: getStatusColor(option.id),
                color: getStatusTextColor(option.id),
              }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ProjectStatusSelector;
